import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { useClients, useCreateProject, useProjects } from '../hooks/useOps';
import type { Project } from '../api/types';
import { formatIDR, humanizeStatus, statusColor } from '../lib/format';

interface MilestoneDraft {
  name: string;
  amount: number;
}

export function ProjectsPage() {
  const { data, isLoading, error } = useProjects();
  const { data: clients } = useClients();
  const createProject = useCreateProject();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [name, setName] = useState('');
  const [milestones, setMilestones] = useState<MilestoneDraft[]>([{ name: 'DP', amount: 0 }]);
  const [formError, setFormError] = useState<string | null>(null);

  const clientName = useMemo(() => {
    const map = new Map((clients ?? []).map((c) => [c.id, c.name]));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  }, [clients]);

  const openCreate = () => {
    setClientId('');
    setName('');
    setMilestones([{ name: 'DP', amount: 0 }]);
    setFormError(null);
    setOpen(true);
  };

  const submit = async () => {
    try {
      const project = await createProject.mutateAsync({
        clientId,
        name,
        milestones: milestones.map((m) => ({ name: m.name, amount: m.amount })),
      });
      setOpen(false);
      // Straight into the workspace: the next step is always planning the work.
      navigate(`/projects/${(project as Project).id}/plan`);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const columns: GridColDef<Project>[] = [
    { field: 'code', headerName: 'Code', minWidth: 140 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
    {
      field: 'clientId',
      headerName: 'Client',
      minWidth: 160,
      valueGetter: (v) => clientName(v as string),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => (
        <Chip size="small" label={humanizeStatus(p.value)} color={statusColor(p.value)} variant="outlined" />
      ),
    },
    {
      field: 'contractValue',
      headerName: 'Contract',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => formatIDR(v as string),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Projects"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New project
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ height: 600 }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          onRowClick={(p) => navigate(`/projects/${p.row.id}`)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      {/* Create project */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New project</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              select
              label="Client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
            >
              {(clients ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <Typography variant="subtitle2">Payment milestones</Typography>
            {milestones.map((m, i) => (
              <Stack direction="row" gap={1} key={i} alignItems="center">
                <TextField
                  label="Name"
                  value={m.name}
                  onChange={(e) =>
                    setMilestones(milestones.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))
                  }
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Amount (DPP)"
                  type="number"
                  value={m.amount}
                  onChange={(e) =>
                    setMilestones(
                      milestones.map((x, idx) => (idx === i ? { ...x, amount: Number(e.target.value) } : x)),
                    )
                  }
                  size="small"
                  sx={{ width: 170 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setMilestones(milestones.filter((_, idx) => idx !== i))}
                  disabled={milestones.length === 1}
                  aria-label="remove milestone"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setMilestones([...milestones, { name: '', amount: 0 }])}
            >
              Add milestone
            </Button>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={!clientId || !name.trim() || createProject.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
